/// <reference types="office-js" />
/* global Word */
import { useState, useCallback } from 'react';
import { getTemplateBase64 } from '../baseTemplate';
import useUiStore from '../store/uiStore';
import { logger } from '../../core/logging';
import { WordApiError } from '../../core/errors/WordApiError';
import { persistTemplateId } from '../../word/document/templateMetadata';
import { useActiveTemplate } from './useActiveTemplate';

export function useApplyTemplate() {
  const showToast = useUiStore((s) => s.showToast);
  const { profile: activeProfile } = useActiveTemplate();
  const [isApplying, setIsApplying] = useState(false);

  const apply = useCallback(async () => {
    if (isApplying) return;
    setIsApplying(true);
    const startedAt = Date.now();
    try {
      logger.info('Apply template requested', {
        templateId: activeProfile.id,
        sourceKey: activeProfile.templateSourceKey,
      });

      const templateBase64 = getTemplateBase64(activeProfile.templateSourceKey);
      if (!templateBase64) {
        logger.error('Template source missing — nothing to apply', {
          templateId: activeProfile.id,
          sourceKey: activeProfile.templateSourceKey,
        });
        showToast({ level: 'error', message: `Template not available: ${activeProfile.name}` });
        return;
      }
      await Word.run(async (context: Word.RequestContext) => {
        const newDoc = context.application.createDocument(templateBase64);
        await context.sync();
        newDoc.open();
        await context.sync();
        await persistTemplateId(context, activeProfile.id);
      });
      logger.info('Template applied', {
        templateId: activeProfile.id,
        durationMs: Date.now() - startedAt,
      });
      showToast({ level: 'success', message: 'Template opened as new document' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      showToast({ level: 'error', message });
      logger.error('Apply template failed', {
        templateId: activeProfile.id,
        durationMs: Date.now() - startedAt,
        error: err,
      });
      if (err instanceof WordApiError) {
        // noop additional handling for now
      }
    } finally {
      setIsApplying(false);
    }
  }, [activeProfile.id, activeProfile.name, activeProfile.templateSourceKey, isApplying, showToast]);

  return { apply, isApplying };
}
